export const LOCALES = {
  EN: "en", // 英语（默认语言）
  ZHTW: "zh-tw", // 中文繁体（使用繁体汉字）
} as const;

export type Locale = (typeof LOCALES)[keyof typeof LOCALES];

/**
 * 检查是否为有效的语言代码
 * @param locale 语言代码
 * @returns 是否为有效语言
 */
export function isValidLocale(locale: string): locale is Locale {
  return Object.values(LOCALES).includes(locale as Locale);
}

/**
 * 归一化任意来源的语言码（后端 `en-US` / 系统 `zh-Hant-TW` / cookie `zh-tw`）到本站两档之一。
 *
 * 与 {@link isValidLocale} 的区别：本函数**宽松匹配**（大小写、地区后缀、`zh-Hant` 系变体都能落档），
 * 但**识别不了就返回 null 而非兜底 `en`** —— 兜底会让「后端语言为空」与「后端语言就是英文」不可区分，
 * 语言对账（见 `useSyncOwnerLocale`）就永远不会补写。要兜底由调用方显式决定。
 *
 * 归档规则与原生 `AppLocale.parse`（`kmp-app/.../i18n/AppLocale.kt`）保持一致。
 */
export function normalizeLocaleCode(input?: string | null): Locale | null {
  const code = (input ?? "").trim().toLowerCase();
  if (!code) {
    return null;
  }
  if (
    code.startsWith("zh-tw") ||
    code.startsWith("zh-hk") ||
    code.startsWith("zh-hant")
  ) {
    return LOCALES.ZHTW;
  }
  if (code.startsWith("en")) {
    return LOCALES.EN;
  }
  return null;
}
